/**
 * Error Master List Page
 * SaaS data table view.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'sharedUi/Tag';
import { Tooltip } from 'sharedUi/Tooltip';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Dialog } from 'primereact/dialog';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';

import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../hooks/useDebounce';
import { errorMasterService } from '../../services/error-master.service';
import { valueSetsService } from '../../services/value-sets.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { ErrorMaster, ErrorMasterSearchParams } from '../../types';
import ErrorMasterDeleteDialog from '../../components/error-master/error-master-delete-dialog';
import { ProjectCell } from '../../components/common/project-cell';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { formatDate, formatTimestamp, formatDateTimeSeconds } from 'sharedUi/dateUtils';
import { useUserAuditNames } from '../../hooks/useUserLookup';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'errorCode', header: 'Error Code', filter: true },
];

const ErrorMasterList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({ entity: 'errorMaster', basePath: '/error-master' });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.ERROR_MASTER) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.ERROR_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.ERROR_MASTER);

  const [errorMasters, setErrorMasters] = useState<(ErrorMaster & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<ErrorMaster | null>(null);
  // Resolve the detail dialog's audit-actor ObjectIds to user names.
  const { createdByName: detailCreatedByName, updatedByName: detailUpdatedByName } =
    useUserAuditNames((detailData as any)?.createdBy, (detailData as any)?.updatedBy);

  // Filters
  const [searchFilter, setSearchFilter] = useState('');
  const [errorSeverityFilter, setErrorSeverityFilter] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [errorTypeFilter, setErrorTypeFilter] = useState('');

  // Project (app) scoping — filter dropdown + re-scope action for admins.
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showGlobal, setShowGlobal] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states (server-side)
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);

  // Per-column filters (SaaS data table)
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearch = useDebounce(searchFilter, 500);

  const showError = useCallback((msg: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 10000 });
  }, []);

  // Value-sets
  const { data: severityItems = [] } = useQuery({ queryKey: ['value-sets', 'errorSeverity'], queryFn: () => valueSetsService.getItems('errorSeverity', 'en'), staleTime: 5 * 60 * 1000 });
  const { data: sourceTypeItems = [] } = useQuery({ queryKey: ['value-sets', 'sourceType'], queryFn: () => valueSetsService.getItems('sourceType', 'en'), staleTime: 5 * 60 * 1000 });
  const { data: errorTypeItems = [] } = useQuery({ queryKey: ['value-sets', 'errorType'], queryFn: () => valueSetsService.getItems('errorType', 'en'), staleTime: 5 * 60 * 1000 });

  const severityOptions = [{ label: 'All Severities', value: '' }, ...severityItems.map((i) => ({ label: i.label, value: i.code }))];
  const sourceTypeOptions = [{ label: 'All Source Types', value: '' }, ...sourceTypeItems.map((i) => ({ label: i.label, value: i.code }))];
  const errorTypeOptions = [{ label: 'All Error Types', value: '' }, ...errorTypeItems.map((i) => ({ label: i.label, value: i.code }))];

  const fetchData = useCallback(async (
    page = 1,
    size = pageSize,
    sortBy: string | null = sortField,
    order: 1 | -1 | null = sortOrder,
    activeFilters: Record<string, any> = filters
  ) => {
    try {
      setLoading(true);
      const params: ErrorMasterSearchParams = { page, pageSize: size };
      if (debouncedSearch) params.q = debouncedSearch;
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = order === 1 ? 'asc' : 'desc';
      }
      if (errorSeverityFilter) params.errorSeverity = errorSeverityFilter;
      if (sourceTypeFilter) (params as any).sourceType = sourceTypeFilter;
      if (errorTypeFilter) (params as any).errorType = errorTypeFilter;
      if (selectedProjectId) (params as any).app_id = selectedProjectId;
      (params as any).include_global = showGlobal;
      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;
      const response = await errorMasterService.getAll(params);
      setErrorMasters((response.data || []).map((em) => ({ ...em, id: em.id || em._id || '' })));
      setTotalRecords(response.pagination?.totalItems ?? (response.pagination as any)?.total ?? response.total ?? 0);
    } catch (error) {
      showError(extractErrorMessage(error, 'Failed to load error masters'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, errorSeverityFilter, sourceTypeFilter, errorTypeFilter, selectedProjectId, showGlobal, pageSize, sortField, sortOrder, filters, showError]);

  useEffect(() => { setFirst(0); fetchData(1); }, [debouncedSearch, errorSeverityFilter, sourceTypeFilter, errorTypeFilter, selectedProjectId, showGlobal]); // eslint-disable-line

  // Handle pagination event
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchData(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort event - server-side sorting
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchData(1, pageSize, newSortField, newSortOrder);
  };

  // Handle per-column filter event
  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchData(1, pageSize, sortField, sortOrder, next);
  };

  const handleDeleteClick = (id: string) => { setSelectedErrorId(id); setDeleteDialogOpen(true); };
  const handleDeleteConfirm = async () => {
    if (!selectedErrorId) return;
    try {
      setDeleteLoading(true);
      await errorMasterService.delete(selectedErrorId);
      setDeleteDialogOpen(false); setSelectedErrorId(null);
      fetchData(Math.floor(first / pageSize) + 1, pageSize);
    } catch (error: any) {
      setDeleteDialogOpen(false); showError(extractErrorMessage(error, 'Failed to delete'));
    } finally { setDeleteLoading(false); }
  };

  // Export to styled Excel
  const [exporting, setExporting] = useState(false);
  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx-js-style');

      // Fetch all records matching current filters
      const allData: any[] = [];
      let pg = 1;
      let hasMore = true;
      while (hasMore) {
        const params: any = { page: pg, pageSize: 200 };
        if (debouncedSearch) params.q = debouncedSearch;
        if (errorSeverityFilter) params.errorSeverity = errorSeverityFilter;
        if (sourceTypeFilter) params.sourceType = sourceTypeFilter;
        if (errorTypeFilter) params.errorType = errorTypeFilter;
        const res = await errorMasterService.getAll(params);
        allData.push(...(res.data || []));
        const total = res.pagination?.totalItems ?? res.total ?? 0;
        hasMore = allData.length < total;
        pg++;
        if (pg > 100) break;
      }

      // Styles
      const titleStyle = { font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '4F46E5' } }, alignment: { horizontal: 'center' } };
      const headerStyle = { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '374151' } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: '9CA3AF' } } } };
      const sevColors: Record<string, string> = { CRITICAL: 'DC2626', HIGH: 'EA580C', MEDIUM: 'CA8A04', LOW: '2563EB' };

      // Build rows
      const headers = ['Error Code', 'Type', 'Severity', 'Message (EN)', 'Status', 'Log', 'Source Type', 'Source Name', 'App Code', 'Business Area', 'Technical Area', 'Tool', 'Partner System', 'Third Party', 'Created'];
      const rows = allData.map((em: any) => {
        const enMsg = em.messages?.find((m: any) => m.language === 'en');
        return [
          em.errorCode || '', (em as any).errorType || '', em.errorSeverity || '',
          enMsg?.template || em.messages?.[0]?.template || '',
          em.isActive ? 'Active' : 'Inactive', em.log ? 'On' : 'Off',
          (em as any).sourceType || '', (em as any).sourceName || '',
          em.businessArea || '', em.technicalArea || '', em.tool || '',
          em.partnerSystem || '', em.thirdParty || '',
          em.createdAt ? formatDate(em.createdAt) : '',
        ];
      });

      // Title row
      const sheetData = [['Error Master Export', ...Array(headers.length - 1).fill('')], headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws['!cols'] = [{ wch: 48 }, { wch: 12 }, { wch: 12 }, { wch: 50 }, { wch: 10 }, { wch: 6 }, { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 12 }];
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];

      // Apply styles
      const applyStyle = (row: number, col: number, style: any) => {
        const addr = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[addr]) ws[addr] = { v: '', t: 's' };
        ws[addr].s = style;
      };

      // Title
      for (let c = 0; c < headers.length; c++) applyStyle(0, c, titleStyle);
      // Headers
      for (let c = 0; c < headers.length; c++) applyStyle(1, c, headerStyle);

      // Data rows — stripe + severity color
      for (let i = 0; i < rows.length; i++) {
        const rowIdx = i + 2;
        const bg = i % 2 === 0 ? 'FFFFFF' : 'F9FAFB';
        for (let c = 0; c < headers.length; c++) {
          const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
          if (!ws[addr]) continue;
          ws[addr].s = { font: { sz: 9 }, fill: { fgColor: { rgb: bg } }, border: { bottom: { style: 'thin', color: { rgb: 'E5E7EB' } } } };
        }
        // Color severity cell (col 2)
        const sevAddr = XLSX.utils.encode_cell({ r: rowIdx, c: 2 });
        if (ws[sevAddr]) {
          const sev = (ws[sevAddr].v || '').toString().toUpperCase();
          if (sevColors[sev]) {
            ws[sevAddr].s = { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: sevColors[sev] } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: 'E5E7EB' } } } };
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Error Master');
      XLSX.writeFile(wb, 'error-master-export.xlsx');
    } catch (err) {
      console.error('Export failed:', err);
      toast.current?.show({ severity: 'error', summary: 'Export Failed', detail: 'Could not generate Excel file', life: 3000 });
    } finally {
      setExporting(false);
    }
  }, [debouncedSearch, errorSeverityFilter, sourceTypeFilter, errorTypeFilter]);

  const getSeverityTag = (sev: string) => {
    const s = (sev || '').toUpperCase();
    const map: Record<string, 'danger' | 'warning' | 'info' | 'success'> = { CRITICAL: 'danger', HIGH: 'warning', MEDIUM: 'info', LOW: 'success' };
    return s ? <Tag value={s} severity={map[s] || 'secondary' as any} className="text-xs" /> : <span style={{ fontSize: '0.75rem' }} className="text-gray-400">-</span>;
  };

  // Table columns
  const columns: SaasColumn<ErrorMaster & { id: string }>[] = useMemo(() => {
    const cols: SaasColumn<ErrorMaster & { id: string }>[] = [
      {
        field: 'errorCode', header: 'Error Code', sortable: true, filter: true, hideable: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.8rem' }} className="font-semibold text-ink break-all">{r.errorCode}</span>,
      },
      {
        field: 'errorType', header: 'Type', sortable: true,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-600">{(r as any).errorType || '-'}</span>,
      },
      {
        field: 'errorSeverity', header: 'Severity', sortable: true,
        body: (r: ErrorMaster) => getSeverityTag(r.errorSeverity),
      },
      {
        field: 'isActive', header: 'Status', sortable: true,
        body: (r: ErrorMaster) => <Tag value={r.isActive ? 'Active' : 'Inactive'} severity={r.isActive ? 'success' : 'secondary'} className="text-xs" />,
      },
      {
        field: 'log', header: 'Log',
        body: (r: ErrorMaster) => <Tag value={r.log ? 'On' : 'Off'} severity={r.log ? 'success' : 'secondary'} className="text-xs" />,
      },
      {
        field: 'businessArea', header: 'Business Area', filter: true, defaultVisible: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-600 truncate block max-w-[120px]" title={r.businessArea || ''}>{r.businessArea || '-'}</span>,
      },
      {
        field: 'technicalArea', header: 'Technical Area', filter: true, defaultVisible: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-600 truncate block max-w-[120px]" title={r.technicalArea || ''}>{r.technicalArea || '-'}</span>,
      },
      {
        field: 'tool', header: 'Tool', defaultVisible: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-600">{r.tool || '-'}</span>,
      },
      {
        field: 'partnerSystem', header: 'Partner System', filter: true, defaultVisible: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-600 truncate block max-w-[120px]" title={r.partnerSystem || ''}>{r.partnerSystem || '-'}</span>,
      },
      {
        field: 'thirdParty', header: 'Third Party', filter: true, defaultVisible: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-600">{r.thirdParty || '-'}</span>,
      },
      {
        field: 'createdAt', header: 'Created', sortable: true, defaultVisible: false,
        body: (r: ErrorMaster) => <span style={{ fontSize: '0.75rem' }} className="text-gray-500 whitespace-nowrap">{formatDateTimeSeconds(r.createdAt)}</span>,
      },
    ];

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    // Actions column
    cols.push({
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r: ErrorMaster & { id: string }) => {
        const isDeleted = (r as any).isDelete || r.isDeleted;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              type="button"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors action-view-btn"
              onClick={() => navigateToView(r.id)}
              data-testid="ErrorMasterList.Action.View"
              data-pr-tooltip="View"
              data-pr-position="top"
            >
              <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
            </button>
            {!isDeleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    type="button"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors action-edit-btn"
                    onClick={() => navigateToEdit(r.id)}
                    data-testid="ErrorMasterList.Action.Edit"
                    data-pr-tooltip="Edit"
                    data-pr-position="top"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    type="button"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors action-delete-btn"
                    onClick={() => handleDeleteClick(r.id)}
                    data-testid="ErrorMasterList.Action.Delete"
                    data-pr-tooltip="Delete"
                    data-pr-position="top"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, canManageProjects, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.ERROR_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-ErrorMaster-List">

        <PageHeader
          embedded={embedded}
          icon="pi pi-exclamation-triangle"
          title="Error Master"
          subtitle="Manage error definitions, severity levels, and localized messages"
          testId="ErrorMaster.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="error-master-v2"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={errorMasters}
            loading={loading}
            dataKey="id"
            emptyMessage="No error masters found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchFilter}
            onGlobalSearchChange={setSearchFilter}
            globalSearchPlaceholder="Search error code or message..."
            testId="ErrorMasterList"
            toolbarStart={
              <div className="flex flex-wrap items-center gap-2">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))}
                    onChange={(e: { value: string }) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '170px' }}
                    data-testid="ErrorMasterList.Filter.Project"
                  />
                )}
                <Dropdown value={errorSeverityFilter} options={severityOptions} onChange={(e: { value: string }) => setErrorSeverityFilter(e.value || '')} placeholder="Severity" style={{ minWidth: '150px' }} resetFilterOnHide data-testid="ErrorMasterList.Filter.Severity" />
                <Dropdown value={errorTypeFilter} options={errorTypeOptions} onChange={(e: { value: string }) => setErrorTypeFilter(e.value || '')} placeholder="Error Type" style={{ minWidth: '150px' }} resetFilterOnHide data-testid="ErrorMasterList.Filter.Type" />
                <Dropdown value={sourceTypeFilter} options={sourceTypeOptions} onChange={(e: { value: string }) => setSourceTypeFilter(e.value || '')} placeholder="Source Type" style={{ minWidth: '150px' }} resetFilterOnHide data-testid="ErrorMasterList.Filter.Source" />
                {/* Last in the row: the dropdowns beside it NARROW the list, this
                    WIDENS it, so it sits after them rather than among them.
                    Always available — a single-project (or core-admin) user needs
                    it too; only the project PICKER is gated by >1 project. */}
                <label className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  <InputSwitch
                    checked={showGlobal}
                    onChange={(e: any) => setShowGlobal(!!e.value)}
                    data-testid="ErrorMasterList.Toggle.ShowGlobal"
                  />
                  Show core
                </label>
                {(errorSeverityFilter || sourceTypeFilter || errorTypeFilter) && (
                  <button aria-label="Clear filters" type="button" onClick={() => { setErrorSeverityFilter(''); setSourceTypeFilter(''); setErrorTypeFilter(''); }} className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-red-500 shadow-sm transition-colors hover:text-red-700 dark:border-gray-700 dark:bg-gray-800" title="Clear filters">
                    <i className="pi pi-times-circle" style={{ fontSize: '1rem' }} />
                  </button>
                )}
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <button aria-label="Export to Excel"
                  type="button"
                  onClick={handleExportExcel}
                  disabled={exporting}
                  className="flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export to Excel"
                  data-testid="ErrorMasterList.Button.Export"
                >
                  <i className={`pi ${exporting ? 'pi-spin pi-spinner' : 'pi-download'} text-green-600`} style={{ fontSize: '0.875rem' }} />
                </button>
                <RefreshButton
                  onClick={() => fetchData(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                />
                {canCreate && (
                  <Button
                    label="Create Error"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="ErrorMasterList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        <Tooltip target="[data-pr-tooltip]" />
      </div>

      {/* Detail Dialog */}
      <Dialog
        visible={detailOpen}
        onHide={() => { setDetailOpen(false); setDetailData(null); }}
        header={
          <div className="flex items-center gap-2">
            <i className="pi pi-file text-indigo-500" style={{ fontSize: '1.1rem' }} />
            <span style={{ fontSize: '1rem', fontWeight: 600 }}>Error Master Details</span>
            {detailData?.errorCode && <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: '#4f46e5', color: '#fff', padding: '2px 8px', borderRadius: '4px' }}>{detailData.errorCode}</span>}
          </div>
        }
        style={{ width: '750px', maxHeight: '90vh' }}
        modal dismissableMask draggable={false} resizable={false}
        footer={
          <div className="flex justify-end gap-2">
            {detailData && canUpdate && <Button label="Edit" icon="pi pi-pencil" size="small" severity="info" onClick={() => { setDetailOpen(false); navigateToEdit(detailData.id || (detailData as any)._id); }} />}
            <Button label="View Full" icon="pi pi-eye" size="small" onClick={() => { setDetailOpen(false); if (detailData) navigateToView(detailData.id || (detailData as any)._id); }} />
            <Button label="Close" icon="pi pi-times" size="small" severity="secondary" onClick={() => setDetailOpen(false)} />
          </div>
        }
      >
        {detailData && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {getSeverityTag(detailData.errorSeverity)}
              {(detailData as any).errorType && <Tag value={(detailData as any).errorType} />}
              {(detailData as any).sourceType && <span style={{ fontSize: '0.8rem', color: '#4b5563', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>{(detailData as any).sourceType}</span>}
              <Tag value={detailData.isActive ? 'Active' : 'Inactive'} severity={detailData.isActive ? 'success' : 'secondary'} />
              {detailData.log !== undefined && <Tag value={detailData.log ? 'Log: On' : 'Log: Off'} severity={detailData.log ? 'success' : 'secondary'} />}
            </div>

            {detailData.messages && detailData.messages.length > 0 && (
              <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '0.5rem', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f46e5', marginBottom: '0.5rem' }}>Localized Messages</p>
                {detailData.messages.map((msg, i) => (
                  <div key={i} style={{ marginBottom: '0.375rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginRight: '0.5rem', background: '#fff', padding: '1px 6px', borderRadius: '3px' }}>{msg.language}</span>
                    <span style={{ fontSize: '0.875rem', color: '#374151' }}>{msg.template}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
              {([
                ['Error Code', detailData.errorCode], ['Error Type', (detailData as any).errorType],
                ['Severity', detailData.errorSeverity], ['Source Type', (detailData as any).sourceType],
                ['Source Name', (detailData as any).sourceName],
                ['Notification ID', (detailData as any).notificationId], ['Business Area', detailData.businessArea],
                ['Technical Area', detailData.technicalArea], ['Tool', detailData.tool],
                ['Partner System', detailData.partnerSystem], ['Third Party', detailData.thirdParty],
                ['Logging', detailData.log ? 'Enabled' : 'Disabled'], ['Active', detailData.isActive ? 'Yes' : 'No'],
                ['Created By', detailCreatedByName], ['Created At', detailData.createdAt ? formatTimestamp(detailData.createdAt) : null],
                ['Updated By', detailUpdatedByName], ['Updated At', detailData.updatedAt ? formatTimestamp(detailData.updatedAt) : null],
              ] as [string, any][]).map(([label, val]) => (
                <div key={label} style={{ padding: '0.25rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 500 }}>{label}</p>
                  <p style={{ fontSize: '0.875rem', color: '#1f2937', wordBreak: 'break-all' }}>{val || '-'}</p>
                </div>
              ))}
            </div>

            {(detailData.devMessage || detailData.helpLink || detailData.videoLink) && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem' }}>
                <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>Developer Information</p>
                {detailData.devMessage && <div style={{ marginBottom: '0.375rem' }}><p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Dev Message</p><p style={{ fontSize: '0.875rem', color: '#374151', wordBreak: 'break-all' }}>{detailData.devMessage}</p></div>}
                {detailData.helpLink && <div style={{ marginBottom: '0.375rem' }}><p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Help Link</p><a href={detailData.helpLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#2563eb', wordBreak: 'break-all' }}>{detailData.helpLink}</a></div>}
                {detailData.videoLink && <div><p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Video Link</p><a href={detailData.videoLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#2563eb', wordBreak: 'break-all' }}>{detailData.videoLink}</a></div>}
              </div>
            )}
          </div>
        )}
      </Dialog>

      <ErrorMasterDeleteDialog errorId={deleteDialogOpen ? selectedErrorId : null} onCancel={() => { setDeleteDialogOpen(false); setSelectedErrorId(null); }} onConfirm={handleDeleteConfirm} deleting={deleteLoading} testId="ErrorMasterList.Dialog.Delete" />
    </PermissionGuard>
  );
};

export default ErrorMasterList;
